import { z } from "zod";
import { checkCustomValue, isCalendarDate } from "./custom-fields.js";
/**
 * Intake forms and task approvals (WP 3.6).
 *
 * A project defines a form: ordered questions, each mapped to one core task
 * field or one of the project's custom fields. Declared once so the server
 * validates a definition and a submission, and the web builds and renders the
 * form, from the same rules.
 */
export const FORM_QUESTION_TYPES = ["short_text", "long_text", "number", "date", "dropdown", "person"];
export const FORM_CORE_TARGETS = ["title", "description", "dueAt", "assignee"];
export const MAX_FORM_QUESTIONS = 30;
export const MAX_FORM_APPROVERS = 10;
export const MAX_TASK_TITLE = 300;
export const MAX_TASK_DESCRIPTION = 20_000;
const questionId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/);
export const formTarget = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("core"), field: z.enum(FORM_CORE_TARGETS) }).strict(),
    z.object({ kind: z.literal("custom"), fieldId: z.string().min(1).max(64) }).strict(),
]);
export const formQuestion = z
    .object({
    id: questionId,
    label: z.string().trim().min(1).max(120),
    help: z.string().trim().max(300).optional(),
    type: z.enum(FORM_QUESTION_TYPES),
    required: z.boolean().default(false),
    target: formTarget,
})
    .strict();
const uuid = z.string().uuid();
export const createFormBody = z
    .object({
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2000).nullable().optional(),
    enabled: z.boolean().default(true),
    questions: z.array(formQuestion).min(1).max(MAX_FORM_QUESTIONS),
    sectionId: uuid.nullable().optional(),
    defaultAssigneeId: uuid.nullable().optional(),
    tagIds: z.array(uuid).max(20).default([]),
    requireApproval: z.boolean().default(false),
    approverIds: z.array(uuid).max(MAX_FORM_APPROVERS).default([]),
})
    .strict();
export const patchFormBody = z
    .object({
    title: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    enabled: z.boolean().optional(),
    questions: z.array(formQuestion).min(1).max(MAX_FORM_QUESTIONS).optional(),
    sectionId: uuid.nullable().optional(),
    defaultAssigneeId: uuid.nullable().optional(),
    tagIds: z.array(uuid).max(20).optional(),
    requireApproval: z.boolean().optional(),
    approverIds: z.array(uuid).max(MAX_FORM_APPROVERS).optional(),
})
    .strict();
export const submitFormBody = z.object({ answers: z.record(z.string().max(40), z.unknown()) }).strict();
/* ─────────────────────────────── definition rules ─────────────────────────────── */
const CORE_TYPES = {
    title: ["short_text"],
    description: ["short_text", "long_text"],
    dueAt: ["date"],
    assignee: ["person"],
};
const CUSTOM_TYPES = {
    text: ["short_text", "long_text"],
    number: ["number"],
    date: ["date"],
    dropdown: ["dropdown"],
    person: ["person"],
};
/**
 * Whether a set of questions makes a usable form against the project's fields:
 * unique ids, exactly one required short-text question for the title, each
 * target used once, and every question's type something its target can hold.
 * Returns the first problem, or null.
 */
export function validateFormQuestions(questions, fields) {
    const fieldById = new Map(fields.map((f) => [f.id, f]));
    const ids = new Set();
    const targets = new Set();
    let titles = 0;
    for (const q of questions) {
        if (ids.has(q.id))
            return "Question ids must be unique.";
        ids.add(q.id);
        const key = q.target.kind === "core" ? `core:${q.target.field}` : `custom:${q.target.fieldId}`;
        if (targets.has(key))
            return "Each task field can be asked only once.";
        targets.add(key);
        if (q.target.kind === "core") {
            if (!CORE_TYPES[q.target.field].includes(q.type))
                return `"${q.label}" cannot be a ${q.type.replace("_", " ")} question for that field.`;
            if (q.target.field === "title") {
                titles++;
                if (!q.required)
                    return "The title question must be required.";
            }
        }
        else {
            const field = fieldById.get(q.target.fieldId);
            if (!field)
                return `"${q.label}" points at a custom field that does not exist.`;
            if (!CUSTOM_TYPES[field.type].includes(q.type))
                return `"${q.label}" cannot be a ${q.type.replace("_", " ")} question for that field.`;
        }
    }
    if (titles !== 1)
        return "A form needs exactly one question for the task title.";
    return null;
}
/** The date a form's due-date answer means: midnight UTC, as the task dialog sends it. */
export const dueAtOf = (day) => `${day}T00:00:00.000Z`;
/**
 * Validates and normalises one answer against its question (and, for a custom
 * target, the field). Whether a person is in the office is the server's check.
 */
export function checkAnswer(question, field, raw) {
    const blank = raw === null || raw === undefined || (typeof raw === "string" && raw.trim() === "");
    if (blank)
        return question.required ? { ok: false, error: `"${question.label}" is required.` } : { ok: true, value: null };
    const fail = (error) => ({ ok: false, error: `"${question.label}": ${error}` });
    switch (question.type) {
        case "short_text":
        case "long_text": {
            if (typeof raw !== "string")
                return fail("Enter text.");
            const text = raw.trim();
            const max = question.target.kind === "core" ? (question.target.field === "title" ? MAX_TASK_TITLE : MAX_TASK_DESCRIPTION) : 2000;
            if (question.type === "short_text" && /[\r\n]/.test(text))
                return fail("Use a single line.");
            if (text.length > max)
                return fail(`Use at most ${max} characters.`);
            return { ok: true, value: text };
        }
        case "number": {
            const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
            if (!Number.isFinite(n) || Math.abs(n) > 1e15)
                return fail("Enter a number.");
            return { ok: true, value: n };
        }
        case "date":
            if (typeof raw !== "string" || !isCalendarDate(raw))
                return fail("Enter a date as YYYY-MM-DD.");
            return { ok: true, value: raw };
        case "dropdown":
        case "person":
            if (typeof raw !== "string" || raw.length > 64)
                return fail(question.type === "person" ? "Choose a person." : "Choose one of the options.");
            break;
    }
    // Dropdown and person: the custom field's own rule decides what is valid.
    if (question.target.kind === "custom" && field) {
        const checked = checkCustomValue(field, raw);
        return checked.ok ? checked : fail(checked.error);
    }
    if (question.type === "person" && !/^[A-Za-z0-9_-]{8,64}$/.test(String(raw)))
        return fail("Choose a person.");
    return { ok: true, value: String(raw) };
}
/* ─────────────────────────────── approvals ─────────────────────────────── */
export const APPROVAL_DECISIONS = ["approved", "changes_requested", "rejected"];
export const APPROVAL_LABEL = {
    pending: "Awaiting approval",
    approved: "Approved",
    changes_requested: "Changes requested",
    rejected: "Rejected",
};
export const MAX_APPROVERS = 10;
export const setApproversBody = z.object({ approverIds: z.array(uuid).max(MAX_APPROVERS) }).strict();
export const decideBody = z
    .object({
    decision: z.enum(APPROVAL_DECISIONS),
    comment: z.string().trim().max(2000).optional(),
})
    .strict();
/**
 * One status from the approvers' decisions: any rejection rejects, otherwise any
 * change request asks for changes, otherwise it is approved once every approver
 * has approved, and pending until then. No approvers means not an approval task.
 */
export function approvalStatusOf(decisions) {
    if (decisions.length === 0)
        return null;
    if (decisions.includes("rejected"))
        return "rejected";
    if (decisions.includes("changes_requested"))
        return "changes_requested";
    return decisions.every((d) => d === "approved") ? "approved" : "pending";
}
//# sourceMappingURL=intake.js.map